import { metrics } from '@seek/metrics-js';

import { RECOMMENDATIONS_CONTENT_DATA_AUTOMATION } from 'src/components/HomePage/Dashboard/SignedInDashboard/DashboardRecommendations/Recommendations';
import { SAVED_SEARCHES_CONTENT_DATA_AUTOMATION } from 'src/components/HomePage/Dashboard/SignedInDashboard/DashboardSaveSearches/SavedSearches';
import { SAVED_JOBS_CONTENT_DATA_AUTOMATION } from 'src/components/HomePage/Dashboard/SignedInDashboard/DashboardSavedJobs/SavedJobs';
import { HOMEPAGE_CONTENT_DATA_AUTOMATION } from 'src/components/HomePage/HomePage';
import { JOB_DETAILS_PAGE_METRICS_IDENTIFIER } from 'src/components/JobDetailsPage/JobDetailsPage';
import { environment } from 'src/config';
import type { Environment } from 'src/config/types';

type Target = {
  selector: string;
  componentName: string;
  using?: string;
};

// Ensure the selector is valid and unique per component
const targetComponents: Target[] = [
  {
    selector: HOMEPAGE_CONTENT_DATA_AUTOMATION,
    componentName: 'HomePage',
  },
  {
    selector: RECOMMENDATIONS_CONTENT_DATA_AUTOMATION,
    componentName: 'HomePageRecommendedJobs',
  },
  {
    selector: SAVED_JOBS_CONTENT_DATA_AUTOMATION,
    componentName: 'HomePageSavedJobs',
  },
  {
    selector: SAVED_SEARCHES_CONTENT_DATA_AUTOMATION,
    componentName: 'HomePageSavedSearches',
  },
  {
    selector: 'job-list-item-link-overlay',
    componentName: 'SearchResultsPageJobListItem',
  },
  {
    selector: JOB_DETAILS_PAGE_METRICS_IDENTIFIER,
    using: 'data-metrics-identifier',
    componentName: 'JobDetailsPageStandalone',
  },
];

/**
 * This function is called in server side to inject the script.
 * This enables us to capture these metrics as early as possible without having to wait for the entire app to load.
 * This also means that we shouldn't try to access any code that isn't available in the global scope directly
 * in the body of this function without serialising it first and passing it when calling observe.toString() below.
 *
 * The function has a list of targets to observe when they're added to the DOM and are intersectable.
 * Once the target is intersecting, we push the component name and the duration to the `window.SEEK_RENDER_METRICS_CAPTURED` array.
 *
 * `registerTimeToRenderCompleteMetricsInterval` is then called in the client side to iterate over the registered metrics
 * and actually fire them to datadog.
 */
export const timeToRenderCompleteMetricsObserver = () => {
  const observe = (env: Environment, targets: Target[]) => {
    if (typeof window === 'undefined') {
      return;
    }

    const shouldLog = env !== 'production' && window.SEEK_RENDER_METRICS_DEBUG;

    /**
     * This ensures that the observer is triggered even if the element is not in the viewport
     */
    const RIDICULOUSLY_LARGE_ROOT_MARGIN =
      '100000px 100000px 100000px 100000px';

    const initialPath = window.location.pathname;
    const shouldStopObserving = () =>
      // Ensure we don't keep observing indefinitely
      performance.now() > 60_000 ||
      // and that we stop observing when the user navigates away from the page
      window.location.pathname !== initialPath;

    const getTargetElement = ({
      selector,
      using = 'data-automation',
    }: Target) => document.querySelector(`[${using}="${selector}"]`);

    window.SEEK_RENDER_METRICS_CAPTURED = [];
    const observed = new Set<Element>();

    for (const target of targets) {
      const intersectionObserver = new IntersectionObserver(
        (entries) => {
          if (shouldStopObserving()) {
            mutationObserver.disconnect();
            intersectionObserver.disconnect();
            return;
          }

          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const duration = performance.now();

              window.SEEK_RENDER_METRICS_CAPTURED.push({
                componentName: target.componentName,
                duration,
              });

              intersectionObserver.disconnect();

              if (shouldLog) {
                // eslint-disable-next-line no-console
                console.table({
                  event: 'SEEK_RENDER_METRICS_CAPTURED',
                  component: target.componentName,
                  duration,
                });
              }
            }
          });
        },
        {
          rootMargin: RIDICULOUSLY_LARGE_ROOT_MARGIN,
        },
      );

      const mutationObserver = new MutationObserver(() => {
        if (shouldStopObserving()) {
          mutationObserver.disconnect();
          intersectionObserver.disconnect();
          return;
        }

        const element = getTargetElement(target);
        if (element && !observed.has(element)) {
          observed.add(element);

          intersectionObserver.observe(element);
          mutationObserver.disconnect();
        }
      });

      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }
  };

  return {
    getScriptTag: () =>
      `<script>(${observe.toString()})("${environment}", ${JSON.stringify(
        targetComponents,
      )});</script>`,
  };
};

export const registerTimeToRenderCompleteMetricsInterval = () => {
  // Safeguard against adding components to SEEK_RENDER_METRICS_CAPTURED that
  // we don't configure
  const WHITELISTED_COMPONENTS = targetComponents.map(
    (target) => target.componentName,
  );

  const metricsSent = new Set();

  const metricsInterval = setInterval(() => {
    if (performance.now() > 60_000) {
      clearInterval(metricsInterval);
      return;
    }

    if (window.SEEK_RENDER_METRICS_CAPTURED?.length) {
      for (const {
        componentName,
        duration,
      } of window.SEEK_RENDER_METRICS_CAPTURED) {
        if (
          !metricsSent.has(componentName) &&
          WHITELISTED_COMPONENTS.includes(componentName)
        ) {
          metrics.timing('component.timeToRenderComplete', duration, [
            `component:${componentName}`,
          ]);

          metricsSent.add(componentName);
        }
      }
    }
  }, 1000);
};
